import type { LoaderArgs, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import * as React from 'react';
import Shop from '~/components/Shop';
import { getUpdatedFiltersSearch } from '~/components/Shop/common';
import ProductsGridWithFilters from '~/components/Shop/ProductsGridWithFilters';
import { scApi } from '~/services/api.server';
import { getUpdatedAuthentication } from '~/services/auth.server';
import { getSession } from '~/services/session.server';
import { ProductTypes } from '~/utils/constants';
import * as gtm from '~/utils/gtm.client';
import { CATEGORIES_PARAM, TYPES_PARAM } from '~/utils/searchParams';
import type { CategoryNode } from '~/utils/shop';
import { getCategories, getCategoriesList, getCategoryNode, getProducts, RemoveUnsetSearchParams } from '~/utils/shop';
import type { SitemapHandle } from '~/utils/sitemap.server';

export const handle: SitemapHandle = {
  getSitemapEntries: (path) => ({
    route: path,
    lastmod: new Date().toISOString(),
    changefreq: 'daily',
    priority: 0.8,
  }),
};

export const meta: MetaFunction = ({ data }) => {
  const { selectedCategory } = data;

  return {
    title: `SwagCharm | ${selectedCategory?.name ?? 'Swag Packs'}`,
    description: selectedCategory?.description,
    keywords: selectedCategory?.keywords,
  };
};

export async function loader({ request, params }: LoaderArgs) {
  const url = new URL(request.url);
  RemoveUnsetSearchParams(url);

  if (!url.searchParams.has(TYPES_PARAM)) {
    url.searchParams.append(TYPES_PARAM, ProductTypes.PACK_TEMPLATE.toString());
    url.searchParams.append(TYPES_PARAM, ProductTypes.CUSTOM_PACK.toString());
    url.searchParams.append(TYPES_PARAM, ProductTypes.CUSTOM_PACK_TEMPLATE.toString());
  }

  const [{ user }, categories] = await Promise.all([
    getUpdatedAuthentication(request),
    getCategories(url.searchParams),
  ]);
  const session = await getSession(request);
  const accountId = session.get('accountId');

  const categorySlugsStr = params['*'] ?? '';
  const categorySlugs = categorySlugsStr.split('/').filter(Boolean);

  const shopPath = '/shop';
  const basePath = `${shopPath}/swag-packs`;

  const packCategory = categories.find((c) => c.slug === 'swag-packs');
  const bulkCategory = categories.filter((c) => c.slug !== 'swag-packs');

  if (!packCategory) {
    return redirect(shopPath);
  }

  const [selectedCategory] = getCategoriesList(packCategory.subcategories, categorySlugs, `${shopPath}`);

  const categoriesList: (CategoryNode | CategoryNode[])[] = [];

  if (bulkCategory) {
    categoriesList.push(bulkCategory.map((bulkCategory) => getCategoryNode(bulkCategory, `${shopPath}/bulk-swag`)));
  }
  categoriesList.push(getCategoryNode(packCategory, shopPath));

  const breadcrumbEntries = [
    { name: 'Home', href: '/' },
    { name: 'Shop', href: shopPath },
    { name: 'Swag Packs', href: basePath },
    ...categoriesList
      .filter((c) => !Array.isArray(c))
      .map((c) => ({ name: (c as CategoryNode).name, href: (c as CategoryNode).href })),
  ];

  if (selectedCategory) {
    url.searchParams.set(CATEGORIES_PARAM, selectedCategory.slug);
  }

  const productsFilters = await scApi.getProductsAvailableFilters(user?.accessToken, accountId, {
    category__in: selectedCategory?.id.toString() ?? '',
  });
  const cleanUrl = new URL(request.url);
  const updatedSearch = getUpdatedFiltersSearch(productsFilters, cleanUrl.searchParams);
  if (cleanUrl.search !== updatedSearch) {
    throw redirect(`${url.pathname}${updatedSearch}`);
  }

  const products = await getProducts(url.searchParams, user?.accessToken, accountId, productsFilters);

  return json({
    isAuthenticated: user !== null,
    products,
    productsFilters,
    categories: categoriesList,
    selectedCategory: selectedCategory || getCategoryNode(packCategory, basePath),
    breadcrumbEntries,
  });
}

export default function Products() {
  const data = useLoaderData<typeof loader>();

  React.useEffect(() => {
    gtm.push_swag_pack_catalog(data.products.results || []);
  }, [data.products]);

  return (
    <Shop {...data}>
      <ProductsGridWithFilters
        categories={data.categories}
        productsFilters={data.productsFilters}
        productGridProps={{ products: data.products, renderCarousel: true, selectedCategory: data.selectedCategory }}
      />
    </Shop>
  );
}
